/** @jsx h */
/** @jsxFrag Fragment */
import { Fragment, h } from "preact";
import { useMemo } from "preact/hooks";
import type { ParamDict } from "@utils/params";
import {
  DatBoolean,
  DatNumber,
  DatSelect,
  DatString,
  DatSubFolder,
  type FoldersContext,
} from "@uiw/dat";
import { DatParamGraphs, GraphsOf } from "@wbaudio/ui/graphs";
import {
  type SampleGen,
  type SampleGenLayer,
  SampleGenLayerDuration,
  SampleGenSynthNames,
  SampleGenSynths,
  type SampleGenTiming,
  SampleGenTimingDefaults,
  type SampleGenTimingType,
  SampleGenTimingTypes,
} from "@wbaudio/core/synth";
import { FxList } from "./fxList.tsx";
import { DatRow, RowButton } from "./row.tsx";

function ParseTimes(text: string): number[] {
  return text.split(/[\s,]+/).filter(Boolean).map(Number).filter((n) =>
    Number.isFinite(n) && n >= 0
  );
}

export interface TimingEditorProps {
  timing: SampleGenTiming;
  length: number;
  onChange: (timing: SampleGenTiming) => void;
}
export function TimingEditor(props: TimingEditorProps) {
  const t = props.timing;
  return (
    <>
      <DatSelect
        label="timing"
        value={t.type}
        options={[...SampleGenTimingTypes]}
        optionLabels={[...SampleGenTimingTypes]}
        onChange={(type: SampleGenTimingType) => props.onChange(SampleGenTimingDefaults(type))}
      />
      {t.type === "once" && (
        <DatNumber
          label="at (s)"
          value={t.at}
          min={0}
          max={props.length}
          step={0.05}
          onChange={(at) => props.onChange({ ...t, at })}
        />
      )}
      {t.type === "at" && (
        <DatString
          label="at (s)"
          value={t.at.join(", ")}
          onChange={(text) => props.onChange({ ...t, at: ParseTimes(text) })}
        />
      )}
      {t.type === "repeat" && (
        <>
          <DatNumber
            label="start (s)"
            value={t.start}
            min={0}
            max={props.length}
            step={0.05}
            onChange={(start) => props.onChange({ ...t, start })}
          />
          <DatNumber
            label="count"
            value={t.n}
            min={1}
            max={64}
            step={1}
            onChange={(n) => props.onChange({ ...t, n })}
          />
          <DatNumber
            label="every (s)"
            value={t.every}
            min={0.05}
            max={props.length}
            step={0.05}
            onChange={(every) => props.onChange({ ...t, every })}
          />
        </>
      )}
    </>
  );
}

export interface LayerEditorProps {
  gen: SampleGen;
  layer: SampleGenLayer;
  index: number;
  count: number;
  length: number;
  folders: FoldersContext;
  path: string;
}
export function LayerEditor(props: LayerEditorProps) {
  const { gen, layer } = props;
  const id = layer.id;
  const def = SampleGenSynths[layer.synth];
  const params = useMemo(
    () =>
      Object.fromEntries(
        Object.entries((def?.params ?? {}) as ParamDict).map((
          [k, p],
        ) => [k, { ...p, value: layer.opts[k] ?? p.value }]),
      ) as ParamDict,
    [layer.synth, layer.opts],
  );
  const duration = SampleGenLayerDuration(layer);

  return (
    <DatSubFolder
      folders={props.folders}
      path={props.path}
      title={`${id}${layer.mute ? " (muted)" : ""}`}
      hasSubfolders
    >
      <DatRow label="layer">
        <RowButton
          active={layer.mute}
          onClick={() => gen.dispatch({ type: "layer/mute", payload: { id } })}
        >
          mute
        </RowButton>
        {props.index > 0 && (
          <RowButton onClick={() => gen.dispatch({ type: "layer/move", payload: { id, by: -1 } })}>
            ▲
          </RowButton>
        )}
        {props.index < props.count - 1 && (
          <RowButton onClick={() => gen.dispatch({ type: "layer/move", payload: { id, by: 1 } })}>
            ▼
          </RowButton>
        )}
        <RowButton onClick={() => gen.dispatch({ type: "layer/remove", payload: { id } })}>
          ✕
        </RowButton>
      </DatRow>
      <DatSelect
        label="synth"
        value={layer.synth}
        options={SampleGenSynthNames}
        optionLabels={SampleGenSynthNames}
        onChange={(synth: string) => gen.dispatch({ type: "layer/synth", payload: { id, synth } })}
      />
      <DatNumber
        label="gain"
        value={layer.gain}
        min={0}
        max={4}
        step={0.01}
        onChange={(g) => gen.dispatch({ type: "layer/gain", payload: { id, gain: g } })}
      />
      <DatBoolean
        label={`bed (fill, ${duration.toFixed(2)}s)`}
        value={layer.bed}
        onChange={() => gen.dispatch({ type: "layer/bed", payload: { id } })}
      />
      {!layer.bed && (
        <>
          <TimingEditor
            timing={layer.timing}
            length={props.length}
            onChange={(timing) => gen.dispatch({ type: "layer/timing", payload: { id, timing } })}
          />
          <DatBoolean
            label="cut"
            value={layer.stop !== null}
            onChange={(on) =>
              gen.dispatch({
                type: "layer/stop",
                payload: { id, stop: on ? Math.max(0.05, duration) : null },
              })}
          />
          {layer.stop !== null && (
            <DatNumber
              label="cut at (s)"
              value={layer.stop}
              min={0.05}
              max={props.length}
              step={0.05}
              onChange={(stop) => gen.dispatch({ type: "layer/stop", payload: { id, stop } })}
            />
          )}
        </>
      )}
      <DatSubFolder folders={props.folders} path={`${props.path}/params`} title="params">
        <DatParamGraphs
          params={params}
          graphs={def ? GraphsOf(def) : []}
          onChange={(opts) => gen.dispatch({ type: "layer/opts", payload: { id, opts } })}
        />
      </DatSubFolder>
      <FxList
        slots={layer.fx}
        folders={props.folders}
        path={`${props.path}/fx`}
        title="effects"
        actions={{
          add: (type) => gen.dispatch({ type: "layer/fx/add", payload: { id, type } }),
          remove: (slot) => gen.dispatch({ type: "layer/fx/remove", payload: { id, slot } }),
          move: (slot, by) => gen.dispatch({ type: "layer/fx/move", payload: { id, slot, by } }),
          bypass: (slot) => gen.dispatch({ type: "layer/fx/bypass", payload: { id, slot } }),
          opts: (slot, opts) =>
            gen.dispatch({ type: "layer/fx/opts", payload: { id, slot, opts } }),
        }}
      />
    </DatSubFolder>
  );
}
